import { Suspense } from 'react';

import { Button } from '@iwoca/orion';
import { FormattedMessage } from 'react-intl';

import styles from './DashboardActionPanel.module.css';
import { TopUpPanel } from './TopUpPanel/TopUpPanel';
import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import { ChevronLeftSVG } from '../../../../svgComponents/chevronLeftSVG/ChevronLeftSVG';
import { RocketSVG } from '../../../../svgComponents/rocketSVG/RocketSVG';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';
import {
    DashboardActionTypes,
    TOP_UP_DRAWDOWN_ACTION
} from '../../../sharedDashboardComponents/LoanProgressBar/DashboardActions.types';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals/MoneyWithDecimals';
import { Rate } from '../../../ui/rate/Rate';
import { useGetDrawdownActionPanelData } from '../../dashboardWidgets/drawdownActionPanel/useGetDrawdownActionPanelData';

export const DashboardActionPanel = ({
    activeDashboardAction,
    showAllDashboardCTAs,
    accountId
}: {
    accountId: string;
    activeDashboardAction: DashboardActionTypes;
    showAllDashboardCTAs?: () => void;
}) => {
    if (activeDashboardAction !== TOP_UP_DRAWDOWN_ACTION) return null;

    return (
        <>
            {showAllDashboardCTAs !== undefined && (
                <Button
                    variant="tertiary"
                    className={styles.backButton}
                    data-testid="action-panel-back-to-dashboard-actions"
                    onClick={showAllDashboardCTAs}
                >
                    <ChevronLeftSVG className={styles.backIcon} />

                    <FormattedMessage
                        id="_.outstanding_view.dashboardActionPanel.goBackToDashboard"
                        defaultMessage="I'll decide later"
                    />
                </Button>
            )}

            <div className={styles.container}>
                <div className={styles.approvalSummaryIcon}>
                    <RocketSVG />
                </div>

                <span
                    data-testid="approval-summary"
                    className={styles.approvalSummaryText}
                >
                    <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                        <Suspense
                            fallback={
                                <div
                                    className="flex-1 rounded-m bg-structure-100 p-l shadow-black-s"
                                    data-testId="loan-overview-loading"
                                >
                                    <MainContentPending />
                                </div>
                            }
                        >
                            <ApprovalSummaryHeader accountId={accountId} />
                        </Suspense>
                    </ErrorBoundary>
                </span>
            </div>

            <TopUpPanel accountId={accountId} />
        </>
    );
};

const ApprovalSummaryHeader = ({ accountId }: { accountId: string }) => {
    const { topUpStatus, topupInterestRate, creditLimit } =
        useGetDrawdownActionPanelData(accountId);
    switch (topUpStatus) {
        case 'INTEREST_ONLY_EXTENSION_APPROVED':
            return (
                <FormattedMessage
                    id="_.outstanding_view.dashboardActionPanel.interestOnlyExtensionSummary"
                    defaultMessage="You can extend your interest-only period by 3 months keeping your interest rate at {interestRate} per month"
                    values={{
                        interestRate: (
                            <span className={styles.topupInterestRate}>
                                <Rate value={topupInterestRate!} />
                            </span>
                        )
                    }}
                />
            );
        case 'INTEREST_ONLY_EXTENSION_APPROVED_WITH_MORE_FUNDS':
            return (
                <FormattedMessage
                    id="_.outstanding_view.dashboardActionPanel.interestOnlyExtensionWithFundsSummary"
                    defaultMessage="You can extend your interest-only period by 3 months and draw down up to {amount} with an interest rate of {interestRate} per month"
                    values={{
                        amount: (
                            <span className={styles.topupApprovedAmount}>
                                <MoneyWithDecimals value={creditLimit} />
                            </span>
                        ),
                        interestRate: (
                            <span className={styles.topupInterestRate}>
                                <Rate value={topupInterestRate!} />
                            </span>
                        )
                    }}
                />
            );
        default:
            return (
                <FormattedMessage
                    id="_.outstanding_view.dashboardActionPanel.topUpApprovalSummary"
                    defaultMessage="You can draw down up to {amount} with an interest rate of {interestRate} per month."
                    values={{
                        amount: (
                            <span className={styles.topupApprovedAmount}>
                                <MoneyWithDecimals value={creditLimit} />
                            </span>
                        ),
                        interestRate: (
                            <span className={styles.topupInterestRate}>
                                <Rate value={topupInterestRate!} />
                            </span>
                        )
                    }}
                />
            );
    }
};
